import { PREVENTION_SETTING_KEYS } from '@kirin/api-client/contracts/policies';

/**
 * Feature-flag state that gates the visibility of individual policy settings.
 * Resolved by `PolicySettingsForm` from `useFeatureFlags` and passed in so the
 * hide-decision stays a pure, testable function.
 */
export interface PolicySettingVisibilityFlags {
  isExtensionCodeScanEnabled: boolean;
  isHookPreventionModeEnabled: boolean;
  isAiMcpToolAnalysisEnabled: boolean;
  isMcpToolResponseScanningEnabled: boolean;
  isCapabilityDetectionEnabled: boolean;
}

/**
 * Decide whether a given policy setting row should be hidden in the settings
 * form. Settings are hidden when their gating feature flag is off, or when they
 * are prevention-related (controlled by the Off/Monitor/Enforce selector
 * instead of a standalone toggle).
 */
export function shouldHidePolicySetting(
  settingKey: string,
  policyKey: string,
  flags: PolicySettingVisibilityFlags
): boolean {
  // Hide extensionsCodeScan if feature flag is disabled
  if (
    !flags.isExtensionCodeScanEnabled &&
    settingKey === 'extensionsCodeScan'
  ) {
    return true;
  }

  // Hide preventSecretLeakage if hook prevention mode flag is disabled
  if (
    !flags.isHookPreventionModeEnabled &&
    settingKey === 'preventSecretLeakage'
  ) {
    return true;
  }

  // Hide enableAIToolAnalysis if AI MCP tool analysis flag is disabled
  if (
    !flags.isAiMcpToolAnalysisEnabled &&
    settingKey === 'enableAIToolAnalysis'
  ) {
    return true;
  }

  // KIR-3157: hide the MCP tool-response scan toggle unless its flag is on
  if (
    !flags.isMcpToolResponseScanningEnabled &&
    settingKey === 'enableToolResponseScan'
  ) {
    return true;
  }

  // KIR-2691: hide dangerousPermissions when capability detection is ON
  // (replaced by the catalog rule engine)
  if (
    flags.isCapabilityDetectionEnabled &&
    settingKey === 'dangerousPermissions'
  ) {
    return true;
  }

  // KIR-2691: hide the legacy excessiveCommandsThreshold when capability
  // detection is ON — the weighted scoring engine supersedes the raw
  // command-count heuristic, so the threshold no longer affects detection.
  if (
    flags.isCapabilityDetectionEnabled &&
    settingKey === 'excessiveCommandsThreshold'
  ) {
    return true;
  }

  // KIR-2691: customCapabilityRules only shown when capability detection is enabled
  if (
    !flags.isCapabilityDetectionEnabled &&
    settingKey === 'customCapabilityRules'
  ) {
    return true;
  }

  // Always hide prevention-related settings (controlled by Off/Monitor/Enforce selector)
  const keys = PREVENTION_SETTING_KEYS[policyKey];
  if (keys?.includes(settingKey)) {
    return true;
  }

  return false;
}
